import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plane } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useConfig } from '@/contexts/SystemConfigContext';
import { cn } from '@/lib/utils';

const STATUS_COLORS = {
  planned: 'bg-slate-100 text-slate-700',
  open: 'bg-emerald-100 text-emerald-700',
  'check-in-open': 'bg-blue-100 text-blue-700',
  locked: 'bg-amber-100 text-amber-700',
  boarding: 'bg-purple-100 text-purple-700',
};

const LoadCard = ({ load, myStatus = [], onManifest }) => {
  const { t } = useTranslation();
  const { formatTime } = useConfig();

  const myEntry = myStatus.find(s => s.loadId === load.loadId);
  const isOnLoad = !!myEntry;
  const isFull = load.slotsAvailable === 0;

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Plane className="h-4 w-4 text-muted-foreground" />
          <span className="font-semibold text-sm">{load.aircraft?.name}</span>
        </div>
        <Badge className={cn('text-[11px]', STATUS_COLORS[load.status] || 'bg-slate-100 text-slate-700')}>
          {t(`funJumper.loadCard.status.${load.status}`, load.status)}
        </Badge>
      </div>

      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          {t('funJumper.loadCard.loadNumber', { number: load.loadNumber })}
        </span>
        <span className="font-medium">{formatTime(load.scheduledAt)}</span>
      </div>

      <div className="text-sm text-muted-foreground">
        {t('funJumper.loadCard.slots', {
          available: load.slotsAvailable,
          total: load.slotsTotal,
        })}
        {load.waitlistCount > 0 && (
          <span className="ml-2 text-amber-600">
            ({t('funJumper.loadCard.waitlistCount', { count: load.waitlistCount })})
          </span>
        )}
      </div>

      {isOnLoad ? (
        <Badge className="w-full justify-center bg-emerald-100 text-emerald-700 border-emerald-300 py-1.5">
          {myEntry.me?.kind === 'waitlisted'
            ? t('funJumper.loadCard.onWaitlist')
            : t('funJumper.loadCard.onThisLoad')}
        </Badge>
      ) : !load.selfManifestable ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Button variant="outline" className="w-full" disabled>
                  {t('funJumper.loadCard.manifest')}
                </Button>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('funJumper.loadCard.notAccepting')}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <Button
          className={cn('w-full', isFull ? 'bg-amber-600 hover:bg-amber-700' : '')}
          onClick={() => onManifest(load)}
        >
          {isFull
            ? t('funJumper.loadCard.joinWaitlist', { position: load.waitlistCount + 1 })
            : t('funJumper.loadCard.manifest')}
        </Button>
      )}
    </Card>
  );
};

export default LoadCard;
